import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Save, Trash2, AlertTriangle, Mail, Plus, XCircle, CheckCircle, Clock, RefreshCw, Link2, Unlink } from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { UPDATE_SITE, ARCHIVE_SITE_TO_TRASH, PERMANENT_DELETE_SITE, VERIFY_SENDER_EMAIL, CHECK_SENDER_EMAIL_VERIFICATION, INITIATE_GSC_OAUTH, DISCONNECT_GSC } from '../../graphql/mutations';
import { GET_GSC_CONNECTION } from '../../graphql/queries';
import { Site, ScanTool, ThresholdFilters, IssueSeverity, EmailSettings, EmailSendCondition, EmailVerificationResponse, GscConnection, GscOAuthUrl } from '../../types';
import { ToolSelector, ScanTool as ToolSelectorTool } from '../scan/ToolSelector';

const client = generateClient();

// Default values for scan settings
const DEFAULT_MAX_PAGES = 200;
const DEFAULT_SCAN_TOOLS: ScanTool[] = ['AXE', 'LIGHTHOUSE', 'PA11Y'];

// Helper to convert between API format (uppercase) and ToolSelector format (lowercase)
const toToolSelectorFormat = (tools: ScanTool[]): ToolSelectorTool[] => {
  return tools.map(t => t.toLowerCase() as ToolSelectorTool);
};

const toApiFormat = (tools: ToolSelectorTool[]): ScanTool[] => {
  return tools.map(t => t.toUpperCase() as ScanTool);
};

interface SiteSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site;
  onSuccess: () => void;
}

export function SiteSettingsModal({ isOpen, onClose, site, onSuccess }: SiteSettingsModalProps) {
  const navigate = useNavigate();
  const [name, setName] = useState(site.name);
  const [url, setUrl] = useState(site.url);
  const [description, setDescription] = useState(site.description || '');

  // Scan settings state
  const [maxPages, setMaxPages] = useState<number>(site.maxPages ?? DEFAULT_MAX_PAGES);
  const [isUnlimited, setIsUnlimited] = useState<boolean>(site.maxPages === null);
  const [pauseAfterCrawl, setPauseAfterCrawl] = useState<boolean>(site.pauseAfterCrawl ?? false);
  const [defaultScanTools, setDefaultScanTools] = useState<ToolSelectorTool[]>(
    toToolSelectorFormat(site.defaultScanTools ?? DEFAULT_SCAN_TOOLS)
  );

  // Threshold filter settings
  const [minSeverity, setMinSeverity] = useState<IssueSeverity | undefined>(
    site.thresholdFilters?.minSeverity
  );
  const [minIssueCount, setMinIssueCount] = useState<number>(
    site.thresholdFilters?.minIssueCount ?? 0
  );
  const [minScore, setMinScore] = useState<number>(
    site.thresholdFilters?.minScore ?? 0
  );
  const [hideAcceptedRisks, setHideAcceptedRisks] = useState<boolean>(
    site.thresholdFilters?.hideAcceptedRisks ?? false
  );

  // Email notification settings
  const [emailEnabled, setEmailEnabled] = useState<boolean>(
    site.emailSettings?.enabled ?? false
  );
  const [emailRecipients, setEmailRecipients] = useState<string[]>(
    site.emailSettings?.recipients ?? []
  );
  const [emailSendCondition, setEmailSendCondition] = useState<EmailSendCondition>(
    site.emailSettings?.sendCondition ?? 'ALWAYS'
  );
  const [emailIncludeChart, setEmailIncludeChart] = useState<boolean>(
    site.emailSettings?.includeChart ?? true
  );
  const [newRecipient, setNewRecipient] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);

  // Sender email state
  const [senderEmail, setSenderEmail] = useState<string>(
    site.emailSettings?.senderEmail ?? ''
  );
  const [senderVerified, setSenderVerified] = useState<boolean>(
    site.emailSettings?.senderVerified ?? false
  );
  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [senderEmailError, setSenderEmailError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);

  // GSC (Google Search Console) settings state
  const [gscEnabled, setGscEnabled] = useState<boolean>(
    site.gscSettings?.enabled ?? false
  );
  const [gscAutoSync, setGscAutoSync] = useState<boolean>(
    site.gscSettings?.autoSync ?? true
  );
  const [gscConnection, setGscConnection] = useState<GscConnection | null>(null);
  const [isConnectingGsc, setIsConnectingGsc] = useState(false);
  const [isDisconnectingGsc, setIsDisconnectingGsc] = useState(false);
  const [gscError, setGscError] = useState<string | null>(null);
  const [isLoadingGscConnection, setIsLoadingGscConnection] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(site.name);
      setUrl(site.url);
      setDescription(site.description || '');
      setMaxPages(site.maxPages ?? DEFAULT_MAX_PAGES);
      setIsUnlimited(site.maxPages === null);
      setPauseAfterCrawl(site.pauseAfterCrawl ?? false);
      setDefaultScanTools(toToolSelectorFormat(site.defaultScanTools ?? DEFAULT_SCAN_TOOLS));
      setMinSeverity(site.thresholdFilters?.minSeverity);
      setMinIssueCount(site.thresholdFilters?.minIssueCount ?? 0);
      setMinScore(site.thresholdFilters?.minScore ?? 0);
      setHideAcceptedRisks(site.thresholdFilters?.hideAcceptedRisks ?? false);
      setEmailEnabled(site.emailSettings?.enabled ?? false);
      setEmailRecipients(site.emailSettings?.recipients ?? []);
      setEmailSendCondition(site.emailSettings?.sendCondition ?? 'ALWAYS');
      setEmailIncludeChart(site.emailSettings?.includeChart ?? true);
      setNewRecipient('');
      setRecipientError(null);
      // Sender email state
      setSenderEmail(site.emailSettings?.senderEmail ?? '');
      setSenderVerified(site.emailSettings?.senderVerified ?? false);
      setNewSenderEmail('');
      setSenderEmailError(null);
      setVerificationMessage(null);
      // GSC state
      setGscEnabled(site.gscSettings?.enabled ?? false);
      setGscAutoSync(site.gscSettings?.autoSync ?? true);
      setGscConnection(null);
      setGscError(null);
      setError(null);
      setShowDeleteConfirm(false);

      // Load GSC connection status
      loadGscConnection();
    }
  }, [isOpen, site]);

  // Load GSC connection status
  const loadGscConnection = async () => {
    setIsLoadingGscConnection(true);
    try {
      const response = await client.graphql({
        query: GET_GSC_CONNECTION,
        variables: { siteId: site.siteId },
      }) as { data: { getGscConnection: GscConnection | null } };

      setGscConnection(response.data.getGscConnection);
    } catch (err) {
      console.error('Error loading GSC connection:', err);
      // Don't show error - just means no connection
    } finally {
      setIsLoadingGscConnection(false);
    }
  };

  if (!isOpen) return null;

  // Handle sender email verification
  const handleVerifySenderEmail = async () => {
    const email = newSenderEmail.trim().toLowerCase();
    if (!email) {
      setSenderEmailError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSenderEmailError('Please enter a valid email address');
      return;
    }

    setIsVerifying(true);
    setSenderEmailError(null);
    setVerificationMessage(null);

    try {
      const response = await client.graphql({
        query: VERIFY_SENDER_EMAIL,
        variables: { siteId: site.siteId, email },
      }) as { data: { verifySenderEmail: EmailVerificationResponse } };

      const result = response.data.verifySenderEmail;
      if (result.success) {
        setSenderEmail(email);
        setSenderVerified(false);
        setNewSenderEmail('');
        setVerificationMessage(result.message);
      } else {
        setSenderEmailError(result.message);
      }
    } catch (err) {
      console.error('Error verifying sender email:', err);
      setSenderEmailError('Failed to send verification email. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle checking verification status
  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    setVerificationMessage(null);

    try {
      const response = await client.graphql({
        query: CHECK_SENDER_EMAIL_VERIFICATION,
        variables: { siteId: site.siteId },
      }) as { data: { checkSenderEmailVerification: EmailVerificationResponse } };

      const result = response.data.checkSenderEmailVerification;
      if (result.success) {
        setSenderVerified(true);
        setVerificationMessage('Email verified successfully! You can now enable notifications.');
      } else {
        setVerificationMessage(result.message);
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      setVerificationMessage('Failed to check verification status. Please try again.');
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // Handle removing sender email
  const handleRemoveSenderEmail = () => {
    setSenderEmail('');
    setSenderVerified(false);
    setVerificationMessage(null);
    setEmailEnabled(false);
    setEmailRecipients([]);
  };

  // Handle GSC OAuth connection
  const handleConnectGsc = async () => {
    setIsConnectingGsc(true);
    setGscError(null);

    try {
      // Build the redirect URI for the OAuth callback
      const redirectUri = `${window.location.origin}/gsc-callback`;

      const response = await client.graphql({
        query: INITIATE_GSC_OAUTH,
        variables: {
          siteId: site.siteId,
          redirectUri,
        },
      }) as { data: { initiateGscOAuth: GscOAuthUrl } };

      const { authorizationUrl, state } = response.data.initiateGscOAuth;

      // Store state and siteId in sessionStorage for the callback
      sessionStorage.setItem('gsc_oauth_state', state);
      sessionStorage.setItem('gsc_oauth_siteId', site.siteId);

      // Redirect to Google OAuth
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error('Error initiating GSC OAuth:', err);
      setGscError('Failed to connect to Google Search Console. Please try again.');
      setIsConnectingGsc(false);
    }
  };

  // Handle GSC disconnect
  const handleDisconnectGsc = async () => {
    setIsDisconnectingGsc(true);
    setGscError(null);

    try {
      await client.graphql({
        query: DISCONNECT_GSC,
        variables: { siteId: site.siteId },
      });

      setGscConnection(null);
      setGscEnabled(false);
    } catch (err) {
      console.error('Error disconnecting GSC:', err);
      setGscError('Failed to disconnect from Google Search Console. Please try again.');
    } finally {
      setIsDisconnectingGsc(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Site name is required');
      return;
    }

    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    // Validate max pages
    if (!isUnlimited) {
      if (!Number.isInteger(maxPages) || maxPages < 1) {
        setError('Max pages must be a positive integer');
        return;
      }
    }

    // Validate scan tools
    if (defaultScanTools.length === 0) {
      setError('At least one scan tool must be selected');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build threshold filters object (only include non-default values)
      const thresholdFilters: ThresholdFilters | null =
        minSeverity || minIssueCount > 0 || minScore > 0 || hideAcceptedRisks
          ? {
              minSeverity: minSeverity || undefined,
              minIssueCount: minIssueCount > 0 ? minIssueCount : undefined,
              minScore: minScore > 0 ? minScore : undefined,
              hideAcceptedRisks: hideAcceptedRisks || undefined,
            }
          : null;

      // Build email settings object
      const emailSettings: EmailSettings | null = emailEnabled || emailRecipients.length > 0
        ? {
            enabled: emailEnabled,
            recipients: emailRecipients,
            sendCondition: emailSendCondition,
            includeChart: emailIncludeChart,
          }
        : null;

      // Build GSC settings object (only enabled/autoSync are editable via UI)
      const gscSettings = gscConnection
        ? {
            enabled: gscEnabled,
            autoSync: gscAutoSync,
          }
        : null;

      await client.graphql({
        query: UPDATE_SITE,
        variables: {
          input: {
            siteId: site.siteId,
            name: name.trim(),
            url: url.trim(),
            description: description.trim() || null,
            maxPages: isUnlimited ? null : maxPages,
            pauseAfterCrawl,
            defaultScanTools: toApiFormat(defaultScanTools),
            thresholdFilters,
            emailSettings,
            gscSettings,
          },
        },
      });

      onSuccess();
    } catch (err) {
      console.error('Error updating site:', err);
      setError('Failed to update site. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await client.graphql({
        query: ARCHIVE_SITE_TO_TRASH,
        variables: { siteId: site.siteId },
      });

      onClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('Error archiving site:', err);
      setError('Failed to move site to trash. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermanentDelete = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await client.graphql({
        query: PERMANENT_DELETE_SITE,
        variables: { siteId: site.siteId },
      });

      onClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('Error deleting site:', err);
      setError('Failed to delete site. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-primary/80" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-card shadow-xl my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-light">
          <h2 className="text-heading-3 font-semibold">Site Settings</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {showDeleteConfirm ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <AlertTriangle size={48} className="mx-auto text-status-error mb-4" />
              <h3 className="text-heading-3 font-semibold mb-2">Delete Site Permanently?</h3>
              <p className="text-body text-gray-medium">
                This will permanently delete <strong>{site.name}</strong> and all associated scan data.
                This action cannot be undone.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-status-error/10 text-status-error rounded-card text-body-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSubmitting}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePermanentDelete}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-status-error text-white rounded-card font-medium
                         hover:bg-status-error/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
            {error && (
              <div className="p-3 bg-status-error/10 text-status-error rounded-card text-body-sm">
                {error}
              </div>
            )}

            {/* Site Name */}
            <div>
              <label htmlFor="name" className="block text-body font-medium mb-2">
                Site Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="input w-full"
                placeholder="My Website"
                disabled={isSubmitting}
              />
            </div>

            {/* URL */}
            <div>
              <label htmlFor="url" className="block text-body font-medium mb-2">
                URL
              </label>
              <input
                id="url"
                type="url"
                value={url}
                onChange={e => setUrl(e.target.value)}
                className="input w-full"
                placeholder="https://example.com"
                disabled={isSubmitting}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-body font-medium mb-2">
                Description <span className="text-gray-medium font-normal">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="input w-full resize-none"
                rows={3}
                placeholder="Brief description of this site"
                disabled={isSubmitting}
              />
            </div>

            {/* Scan Settings Section */}
            <div className="pt-4 border-t border-gray-light">
              <h4 className="text-body font-semibold mb-4">Scan Settings</h4>

              {/* Max Pages */}
              <div className="mb-4">
                <label htmlFor="maxPages" className="block text-body font-medium mb-2">
                  Max Pages to Scan
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="maxPages"
                    type="number"
                    min="1"
                    value={isUnlimited ? '' : maxPages}
                    onChange={e => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value > 0) {
                        setMaxPages(value);
                      }
                    }}
                    className="input w-32"
                    placeholder="200"
                    disabled={isSubmitting || isUnlimited}
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isUnlimited}
                      onChange={e => {
                        setIsUnlimited(e.target.checked);
                        if (!e.target.checked && !maxPages) {
                          setMaxPages(DEFAULT_MAX_PAGES);
                        }
                      }}
                      disabled={isSubmitting}
                      className="h-4 w-4 text-coral rounded focus:ring-coral"
                    />
                    <span className="text-body">Unlimited</span>
                  </label>
                </div>
                <p className="text-caption text-gray-medium mt-1">
                  {isUnlimited
                    ? 'All discovered pages will be scanned'
                    : `Scanning will stop after ${maxPages} pages are processed`}
                </p>
              </div>

              {/* Pause After Crawl */}
              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pauseAfterCrawl}
                    onChange={e => setPauseAfterCrawl(e.target.checked)}
                    disabled={isSubmitting}
                    className="h-4 w-4 text-coral rounded focus:ring-coral"
                  />
                  <div>
                    <span className="text-body font-medium">Pause after crawl by default</span>
                    <p className="text-caption text-gray-medium">
                      Review discovered pages before scanning begins
                    </p>
                  </div>
                </label>
              </div>

              {/* Default Scan Tools */}
              <div>
                <ToolSelector
                  selectedTools={defaultScanTools}
                  onChange={setDefaultScanTools}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Threshold Filter Settings */}
            <div className="pt-4 border-t border-gray-light">
              <h4 className="text-body font-semibold mb-4">Default Result Filters</h4>
              <p className="text-caption text-gray-medium mb-4">
                Set default filters for viewing scan results. These can be overridden when viewing results.
              </p>

              {/* Minimum Severity */}
              <div className="mb-4">
                <label className="block text-body font-medium mb-2">Minimum Severity</label>
                <p className="text-caption text-gray-medium mb-2">
                  Only show issues at this severity or higher
                </p>
                <select
                  value={minSeverity || ''}
                  onChange={e => setMinSeverity((e.target.value as IssueSeverity) || undefined)}
                  disabled={isSubmitting}
                  className="input w-full"
                >
                  <option value="">All severities</option>
                  <option value="CRITICAL">Critical only</option>
                  <option value="SERIOUS">Serious and above</option>
                  <option value="MODERATE">Moderate and above</option>
                  <option value="MINOR">Minor and above (all)</option>
                </select>
              </div>

              {/* Minimum Issue Count */}
              <div className="mb-4">
                <label className="block text-body font-medium mb-2">
                  Minimum Issue Count: {minIssueCount}
                </label>
                <p className="text-caption text-gray-medium mb-2">
                  Only show pages with at least this many issues
                </p>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={minIssueCount}
                  onChange={e => setMinIssueCount(parseInt(e.target.value, 10))}
                  disabled={isSubmitting}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-coral"
                />
              </div>

              {/* Minimum Score (Focus on problem areas) */}
              <div className="mb-4">
                <label className="block text-body font-medium mb-2">
                  Score Threshold: {minScore > 0 ? `< ${minScore}` : 'None'}
                </label>
                <p className="text-caption text-gray-medium mb-2">
                  Only show pages scoring below this value (to focus on problem areas)
                </p>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={minScore}
                  onChange={e => setMinScore(parseInt(e.target.value, 10))}
                  disabled={isSubmitting}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-coral"
                />
              </div>

              {/* Hide Accepted Risks */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideAcceptedRisks}
                    onChange={e => setHideAcceptedRisks(e.target.checked)}
                    disabled={isSubmitting}
                    className="h-4 w-4 text-coral rounded focus:ring-coral"
                  />
                  <div>
                    <span className="text-body font-medium">Hide accepted risks by default</span>
                    <p className="text-caption text-gray-medium">
                      Exclude issues that have been marked as accepted risks
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Email Notification Settings */}
            <div className="pt-4 border-t border-gray-light">
              <div className="flex items-center gap-2 mb-4">
                <Mail size={18} className="text-coral" />
                <h4 className="text-body font-semibold">Email Notifications</h4>
              </div>
              <p className="text-caption text-gray-medium mb-4">
                Receive email reports when scans complete. First, set up a verified sender email address.
              </p>

              {/* Sender Email Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-body font-medium mb-2">
                  Sender Email Address
                </label>
                <p className="text-caption text-gray-medium mb-3">
                  This email will be used as the "From" address for notifications. You must verify this email in AWS SES.
                </p>

                {senderEmail ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      {senderVerified ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <Clock size={18} className="text-yellow-600" />
                      )}
                      <span className="text-body">{senderEmail}</span>
                      <span className={`text-caption px-2 py-0.5 rounded ${
                        senderVerified
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {senderVerified ? 'Verified' : 'Pending Verification'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {!senderVerified && (
                        <button
                          type="button"
                          onClick={handleCheckVerification}
                          disabled={isCheckingVerification || isSubmitting}
                          className="btn-secondary text-sm flex items-center gap-1"
                        >
                          <RefreshCw size={14} className={isCheckingVerification ? 'animate-spin' : ''} />
                          {isCheckingVerification ? 'Checking...' : 'Check Verification'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleRemoveSenderEmail}
                        disabled={isSubmitting}
                        className="btn-secondary text-sm text-status-error hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                    {verificationMessage && (
                      <p className={`text-caption ${senderVerified ? 'text-green-600' : 'text-yellow-600'}`}>
                        {verificationMessage}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={newSenderEmail}
                        onChange={e => {
                          setNewSenderEmail(e.target.value);
                          setSenderEmailError(null);
                        }}
                        placeholder="your-email@example.com"
                        disabled={isVerifying || isSubmitting}
                        className="input flex-1"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleVerifySenderEmail();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleVerifySenderEmail}
                        disabled={isVerifying || isSubmitting || !newSenderEmail.trim()}
                        className="btn-primary"
                      >
                        {isVerifying ? 'Sending...' : 'Verify Email'}
                      </button>
                    </div>
                    {senderEmailError && (
                      <p className="text-caption text-status-error">{senderEmailError}</p>
                    )}
                    {verificationMessage && (
                      <p className="text-caption text-green-600">{verificationMessage}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Notification Settings - Only enabled when sender is verified */}
              <div className={!senderVerified ? 'opacity-50 pointer-events-none' : ''}>
                {!senderVerified && (
                  <p className="text-caption text-yellow-600 mb-4 italic">
                    Verify a sender email address above to enable notification settings.
                  </p>
                )}

                {/* Enable Email Notifications */}
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailEnabled}
                      onChange={e => setEmailEnabled(e.target.checked)}
                      disabled={isSubmitting || !senderVerified}
                      className="h-4 w-4 text-coral rounded focus:ring-coral"
                    />
                    <div>
                      <span className="text-body font-medium">Enable email notifications</span>
                      <p className="text-caption text-gray-medium">
                        Send email reports when scans complete
                      </p>
                    </div>
                  </label>
                </div>

                {emailEnabled && senderVerified && (
                  <>
                    {/* Recipients */}
                    <div className="mb-4">
                      <label className="block text-body font-medium mb-2">
                        Recipients
                      </label>
                      <p className="text-caption text-gray-medium mb-2">
                        Note: In SES sandbox mode, recipient emails must also be verified in AWS SES.
                      </p>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="email"
                          value={newRecipient}
                          onChange={e => {
                            setNewRecipient(e.target.value);
                            setRecipientError(null);
                          }}
                          placeholder="email@example.com"
                          disabled={isSubmitting}
                          className="input flex-1"
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const email = newRecipient.trim().toLowerCase();
                              if (!email) return;
                              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                              if (!emailRegex.test(email)) {
                                setRecipientError('Please enter a valid email address');
                                return;
                              }
                              if (emailRecipients.includes(email)) {
                                setRecipientError('This email is already added');
                                return;
                              }
                              setEmailRecipients([...emailRecipients, email]);
                              setNewRecipient('');
                              setRecipientError(null);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const email = newRecipient.trim().toLowerCase();
                            if (!email) return;
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(email)) {
                              setRecipientError('Please enter a valid email address');
                              return;
                            }
                            if (emailRecipients.includes(email)) {
                              setRecipientError('This email is already added');
                              return;
                            }
                            setEmailRecipients([...emailRecipients, email]);
                            setNewRecipient('');
                            setRecipientError(null);
                          }}
                          disabled={isSubmitting || !newRecipient.trim()}
                          className="btn-secondary px-3"
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                      {recipientError && (
                        <p className="text-caption text-status-error mb-2">{recipientError}</p>
                      )}
                      {emailRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {emailRecipients.map(email => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-body-sm"
                          >
                            {email}
                            <button
                              type="button"
                              onClick={() => setEmailRecipients(emailRecipients.filter(e => e !== email))}
                              disabled={isSubmitting}
                              className="text-gray-medium hover:text-status-error"
                            >
                              <XCircle size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {emailRecipients.length === 0 && (
                      <p className="text-caption text-gray-medium">
                        No recipients added. Add at least one email address to receive notifications.
                      </p>
                    )}
                  </div>

                  {/* Send Condition */}
                  <div className="mb-4">
                    <label className="block text-body font-medium mb-2">
                      When to Send
                    </label>
                    <select
                      value={emailSendCondition}
                      onChange={e => setEmailSendCondition(e.target.value as EmailSendCondition)}
                      disabled={isSubmitting}
                      className="input w-full"
                    >
                      <option value="ALWAYS">After every scan</option>
                      <option value="ON_CHANGE">Only when score changes</option>
                      <option value="ON_REGRESSION">Only when score decreases</option>
                    </select>
                  </div>

                  {/* Include Chart */}
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailIncludeChart}
                        onChange={e => setEmailIncludeChart(e.target.checked)}
                        disabled={isSubmitting}
                        className="h-4 w-4 text-coral rounded focus:ring-coral"
                      />
                      <div>
                        <span className="text-body font-medium">Include score trend chart</span>
                        <p className="text-caption text-gray-medium">
                          Show a chart of score history in the email (requires 2+ scans)
                        </p>
                      </div>
                    </label>
                  </div>
                </>
                )}
              </div>
            </div>

            {/* Google Search Console Settings */}
            <div className="pt-4 border-t border-gray-light">
              <div className="flex items-center gap-2 mb-4">
                <Link2 size={18} className="text-coral" />
                <h4 className="text-body font-semibold">Toxic Backlink Analysis</h4>
              </div>
              <p className="text-caption text-gray-medium mb-4">
                Connect Google Search Console to analyze inbound backlinks for toxicity. This helps identify
                potentially harmful links pointing to your site that could affect your SEO.
              </p>

              {gscError && (
                <div className="mb-4 p-3 bg-status-error/10 text-status-error rounded-card text-body-sm">
                  {gscError}
                </div>
              )}

              {/* Connection Status Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-body font-medium mb-2">
                  Google Search Console Connection
                </label>

                {isLoadingGscConnection ? (
                  <div className="flex items-center gap-2 text-gray-medium">
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Loading connection status...</span>
                  </div>
                ) : gscConnection && gscConnection.status === 'CONNECTED' ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={18} className="text-green-600" />
                      <div>
                        <span className="text-body font-medium">Connected</span>
                        <p className="text-caption text-gray-medium">
                          Property: {gscConnection.gscPropertyUrl}
                        </p>
                        {gscConnection.lastSyncAt && (
                          <p className="text-caption text-gray-medium">
                            Last synced: {new Date(gscConnection.lastSyncAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDisconnectGsc}
                      disabled={isDisconnectingGsc || isSubmitting}
                      className="btn-secondary text-sm text-status-error hover:bg-red-50 flex items-center gap-1"
                    >
                      <Unlink size={14} />
                      {isDisconnectingGsc ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </div>
                ) : gscConnection && gscConnection.status === 'EXPIRED' ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Clock size={18} className="text-yellow-600" />
                      <div>
                        <span className="text-body font-medium text-yellow-700">Connection Expired</span>
                        <p className="text-caption text-gray-medium">
                          Your Google Search Console access has expired. Please reconnect.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleConnectGsc}
                        disabled={isConnectingGsc || isSubmitting}
                        className="btn-primary text-sm"
                      >
                        {isConnectingGsc ? 'Connecting...' : 'Reconnect'}
                      </button>
                      <button
                        type="button"
                        onClick={handleDisconnectGsc}
                        disabled={isDisconnectingGsc || isSubmitting}
                        className="btn-secondary text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-caption text-gray-medium">
                      Not connected. Connect your Google Search Console to enable toxic backlink analysis.
                    </p>
                    <button
                      type="button"
                      onClick={handleConnectGsc}
                      disabled={isConnectingGsc || isSubmitting}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                        />
                      </svg>
                      {isConnectingGsc ? 'Connecting...' : 'Connect Google Search Console'}
                    </button>
                  </div>
                )}
              </div>

              {/* GSC Settings - Only visible when connected */}
              {gscConnection && gscConnection.status === 'CONNECTED' && (
                <div className="space-y-4">
                  {/* Enable Backlink Analysis */}
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gscEnabled}
                        onChange={e => setGscEnabled(e.target.checked)}
                        disabled={isSubmitting}
                        className="h-4 w-4 text-coral rounded focus:ring-coral"
                      />
                      <div>
                        <span className="text-body font-medium">Enable toxic backlink analysis</span>
                        <p className="text-caption text-gray-medium">
                          Analyze inbound backlinks for toxicity when running scans
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Auto-sync Setting */}
                  {gscEnabled && (
                    <div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={gscAutoSync}
                          onChange={e => setGscAutoSync(e.target.checked)}
                          disabled={isSubmitting}
                          className="h-4 w-4 text-coral rounded focus:ring-coral"
                        />
                        <div>
                          <span className="text-body font-medium">Auto-sync backlinks</span>
                          <p className="text-caption text-gray-medium">
                            Automatically fetch and analyze backlinks when scans complete
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-light">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex-1"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Save size={18} />
                    Save Changes
                  </span>
                )}
              </button>
            </div>

            {/* Danger Zone */}
            <div className="pt-4 border-t border-gray-light">
              <h4 className="text-body font-medium text-status-error mb-3">Danger Zone</h4>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-status-warning text-status-warning rounded-card font-medium
                           hover:bg-status-warning/10 transition-colors disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Trash2 size={16} />
                    Move to Trash
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-status-error text-status-error rounded-card font-medium
                           hover:bg-status-error/10 transition-colors disabled:opacity-50"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
